import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';

import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Members from './pages/Members/Members';
import AddMember from './pages/Members/AddMember';
import EditMember from './pages/Members/EditMember';
import MemberProfile from './pages/Members/MemberProfile';
import Attendance from './pages/Attendance/Attendance';
import Payments from './pages/Payments/Payments';
import Supplements from './pages/Supplements/Supplements';
import Expenses from './pages/Expenses/Expenses';
import Reports from './pages/Reports/Reports';
import Settings from './pages/Settings/Settings';

// Guards unauthenticated users to the login page
function PrivateRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading) return <LoadingSpinner message="Loading..." />;
  return token ? children : <Navigate to="/login" replace />;
}

// Redirects logged-in users away from login
function PublicRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading) return <LoadingSpinner message="Loading..." />;
  return token ? <Navigate to="/" replace /> : children;
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden" style={{background:'var(--bg)'}}>
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar setIsOpen={setSidebarOpen} />
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          <Routes>
            <Route path="/"              element={<Dashboard />} />
            <Route path="/members"       element={<Members />} />
            <Route path="/members/add"   element={<AddMember />} />
            <Route path="/members/edit/:id" element={<EditMember />} />
            <Route path="/members/:id"   element={<MemberProfile />} />
            <Route path="/attendance"    element={<Attendance />} />
            <Route path="/payments"      element={<Payments />} />
            <Route path="/supplements"   element={<Supplements />} />
            <Route path="/expenses"      element={<Expenses />} />
            <Route path="/reports"       element={<Reports />} />
            <Route path="/settings"      element={<Settings />} />
            <Route path="*"              element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '12px',
              fontSize: '14px',
              background: '#111D2C',
              color: '#E2EAF4',
              border: '1px solid rgba(0,255,240,0.12)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
            },
            success: { iconTheme: { primary: '#00FFF0', secondary: '#060A10' } },
            error:   { iconTheme: { primary: '#FF4060', secondary: '#fff' } },
          }}
        />
        <Routes>
          {/* Public */}
          <Route path="/login" element={
            <PublicRoute><Login /></PublicRoute>
          } />

          {/* Private — everything else */}
          <Route path="/*" element={
            <PrivateRoute><AppLayout /></PrivateRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
